import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import moment from 'moment'

import DateRange from 'components/Shared/DateRange'
import { useAppDispatch, useAppSelector } from 'redux/Hook'
import 'react-date-range/dist/styles.css' // main css file
import 'react-date-range/dist/theme/default.css' // theme css file
import './OverviewDateRange.scss'
import { FilterItem } from 'types/FilterBuilder'
import { RANGE_TYPES } from 'lib/GlobalConstants'
import { CustomDateRange } from 'types/ReportingDashboardFilter'
import { calculateDateRange, getDateRange } from 'lib/DateHelper'
import { updateFilter } from 'redux/slices/FilterBuilder'
import { trackActions } from 'containers/Helper'
import { intervalType } from 'components/Shared/Helpers/SegmentHelper'

type Props = {
  item: FilterItem
  index: number
  isSharedDashboard: boolean
}

const DateRangeFilter = (props: Props) => {
  const dispatch = useAppDispatch()

  const { item, index, isSharedDashboard } = props
  const {
    features: { features: currentFeatures = [] } = {},
    site: { site: site = { createdAt: '' } } = {},
    user: { user: { adminLocale: adminLocale = '' } = {} } = {},
    filterBuilder: { selectedFilters = [] },
    stakeholderDashboardDetails,
  } = useAppSelector((state) => state)

  const [focusedRange, setFocusedRange] = useState<any>([0, 0])
  const { isDateRangeVisible = false, calculatedRange } = item || {}
  let { dateRange } = item || {}
  const [selectedDateRange, setSelectedDateRange] = React.useState(dateRange)

  const getStartAndEndDate = (
    value: string,
    customDateRange?: CustomDateRange,
  ) => {
    const calculatedRange = getCalculatedDateRange(value, customDateRange)
    const { startDate, endDate } =
      value === RANGE_TYPES.custom
        ? {
            startDate: calculatedRange.split('-')[0].trim(),
            endDate: calculatedRange.split('-')[1].trim(),
          }
        : getDateRange(value, true, site?.createdAt)
    return { startDate, endDate }
  }

  const getFormattedDateDiff = (startDate: any, endDate: any) => {
    const updatedStartDate = moment(moment(startDate).format())
    const updatedEndDate = moment(moment(endDate).format())
    return (
      updatedEndDate.diff(updatedStartDate, 'days') !== 0 &&
      `${updatedEndDate.diff(updatedStartDate, 'days') + 1} days`
    )
  }

  const trackDateRange = (
    value: string,
    customDateRange?: CustomDateRange,
    isInitialRender?: boolean,
  ) => {
    const { startDate, endDate } = getStartAndEndDate(value, customDateRange)
    const featureName = stakeholderDashboardDetails?.accessToken
      ? 'Shared Overview Dashboard'
      : 'Overview Dashboard'

    !isInitialRender &&
      getFormattedDateDiff(startDate, endDate) &&
      trackActions('Date Range Selected', {
        featureName,
        sectionName: 'Dashboard Filters',
        dateInterval: `${intervalType[value]}`,
        daysNumber: getFormattedDateDiff(startDate, endDate),
        ...currentFeatures?.['segment_analytics_admin']?.enabled,
      })
  }

  useEffect(() => {
    dateRange = {
      endDate: moment(moment(dateRange?.endDate)).toDate(),
      startDate: moment(moment(dateRange?.startDate)).toDate(),
      key: dateRange?.key || 'selection',
    } // this had to be done since the date from redux dosent work well while changing the option to custom

    setSelectedDateRange((prevState: any) => ({
      ...prevState,
      ...dateRange,
    }))
  }, [dateRange])

  const getCalculatedDateRange = (
    value: string,
    customDateRange?: CustomDateRange,
  ) => {
    if (value === RANGE_TYPES.allTime)
      return t(`filters.dateRange.ranges.${RANGE_TYPES.allTime}`)

    const updatedDateRange = { ...customDateRange, dateRangeOption: value } || {
      ...dateRange,
      dateRangeOption: value,
    }
    return calculateDateRange(value, updatedDateRange)
  }

  const calculateAndDispatchDateRangeOption = ({
    value,
    customDateRange,
    isInitialRender = false,
  }: {
    value: string
    customDateRange?: CustomDateRange
    isInitialRender?: boolean
  }) => {
    trackDateRange(value, customDateRange, isInitialRender)

    const calculatedRange = getCalculatedDateRange(value, customDateRange)

    if (value !== RANGE_TYPES.custom) {
      dispatch(
        updateFilter({
          item: {
            ...{ calculatedRange, dateRangeOption: value },
            dateRangeOption: value,
          },
          index,
        }),
      )
      dispatch(
        updateFilter({
          item: {
            dateRange: {
              ...{ ...item.dateRange, dateRangeOption: value },
              ...getDateRange(value, true, site?.createdAt),
            },
          },
          index,
        }),
      )
    }
  }

  // Applied locale change on selected range in date filter dropdown
  useEffect(() => {
    const { dateRangeOption: rangeOption, dateRange: range } =
      selectedFilters[0]
    const updatedRange = { ...range, key: 'selection' }
    const calculatedRange = getCalculatedDateRange(
      rangeOption as string,
      updatedRange,
    )

    dispatch(updateFilter({ item: { calculatedRange }, index }))
  }, [adminLocale])

  useEffect(() => {
    !isSharedDashboard &&
      calculateAndDispatchDateRangeOption({
        value: RANGE_TYPES.week,
        isInitialRender: true,
      })
  }, [])

  const isEndDateFocused = (focusNumber: number) => {
    return focusNumber === 1
  }

  const closeDateRangePicker = (value: boolean) => {
    dispatch(updateFilter({ item: { isDateRangeVisible: value }, index }))
  }

  const handleAndSetDateRangeFilter = (dateItem: any) => {
    const { selection } = dateItem
    const calculatedRange = getCalculatedDateRange(
      RANGE_TYPES.custom,
      selection,
    )
    setTimeout(() => {
      closeDateRangePicker(false)
    }, 250)

    dispatch(
      updateFilter({
        item: { calculatedRange, dateRange: selection },
        index,
      }),
    )
    calculateAndDispatchDateRangeOption({
      value: RANGE_TYPES.custom,
      customDateRange: selection,
    })

    dispatch(
      updateFilter({
        item: {
          ...{ calculatedRange, dateRangeOption: RANGE_TYPES.custom },
          dateRangeOption: RANGE_TYPES.custom,
        },
        index,
      }),
    )
  }

  const handleRangeSelection = (value: string) => {
    calculateAndDispatchDateRangeOption({ value })
    if (value === RANGE_TYPES.custom) closeDateRangePicker(true)
  }

  const handleDateRangeChange = (item: any) => {
    // used for custom date range selection
    const { selection, range1 } = item

    if (isEndDateFocused(focusedRange?.[1])) {
      handleAndSetDateRangeFilter(item)
    } else {
      setSelectedDateRange(selection || range1)
    }
  }

  const reportingDatePickerData = {
    isDateRangePickerOpen: isDateRangeVisible,
  }

  return (
    <DateRange
      siteCreatedAt={site?.createdAt}
      adminLocale={adminLocale}
      data={reportingDatePickerData}
      handleDateRangeChangeAsProp={handleDateRangeChange}
      label={`dateRange`}
      setFocusedRangeAsProp={setFocusedRange}
      textFieldOnChange={handleRangeSelection}
      calculatedRange={calculatedRange}
      selectedRangeAsProp={selectedDateRange}
      focusedRangeAsProp={focusedRange}
      defaultOpenAsProp={false}
      closeDateRangePicker={() => closeDateRangePicker(false)}
    />
  )
}

export default DateRangeFilter
